import React, { useEffect, useState } from "react";
import { TAB_TEXT } from "../../utilities/constants";
import { fetchCreateWizard, fetchSearchWizard, fetchWizardList } from "../../api/WizardService";
import MainTabs from "../tabs";
import Footer from "./WizardFooter";
import LoadingSpinner from "../loading";
import WizardListItems from "./WizardList";
import WizardSelectType from "./WizardSelectType";
import WizardStatus from "./WizardStatus";
import WizardFinalStep from "./WizardFinalStep";
import DataNotFound from "../dataNotFound";
import Notification from "../notification";
import { replaceVirtualController } from "../../utilities/helpers";

import "./index.scss";

const WizardPage = ({ data, closeACUModal, showNotificationPopUp }) => {
  const [step, setStep] = useState(1);
  const [wizardList, setWizardList] = useState(null);
  const [selectedWizard, setSelectedWizard] = useState(null);
  const [error, setError] = useState(null);
  const [serialNumberError, setSerialNumberError] = useState(null);
  const [serialNumberLoading, setSerialNumberLoading] = useState(false);
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [deviceDescription, setDeviceDescription] = useState("");
  const [haveSerialNumber, setHaveSerialNumber] = useState(true);
  const [showNotification, setShowNotification] = useState([]);
  const [serial_number, setSerial_number] = useState("");

  useEffect(() => {
    setWizardList(data || []);
    setSelectedWizard({ data: data[0] || [] });
  }, []);

  const handleStepChange = (change) => {
    setStep((prev) => Math.min(3, Math.max(1, prev + change)));
  };

  const handleSearchWithSerialNumber = async (serialNumber) => {
    setSerialNumberLoading(true);
    setShowSearchResult(true);
    try {
      if (selectedWizard?.data) {
        let result = {
          message: {
            title: "Successful!",
            subTitle: [
              "The virtual controller has been created and is ready to be added!",
            ],
            description:
              "You can change the controller name now and provide a description, or you can do it later.",
          },
          data: {
            ...selectedWizard.data,
            defaultName: `${selectedWizard.data.name} VIRTUAL CONTROLLER`,
            comment: "",
            versionNumber: "",
          },
        };

        if (serialNumber) {
          setSerial_number(serialNumber);
          result = await fetchSearchWizard(serialNumber, selectedWizard.data.name);
          result.data.img = selectedWizard.data.img;
        }

        setSelectedWizard(result);
        setDeviceName(replaceVirtualController(`${result?.data?.defaultName}`));
        setSerialNumberError(null);
        handleStepChange(1);
      }
    } catch (error) {
      const { message, data } = error;
      setSerialNumberError({ status: message, ...data });
    } finally {
      setSerialNumberLoading(false);
    }
  };

  const handleCancelStep = () => {
    closeACUModal()
  };

  const handleFinishStep = async () => {
    const data = {
      name: deviceName,
      description: deviceDescription,
      model: selectedWizard.data.name,
    }
    if (serial_number) {
      data.serial_number = serial_number
    }
    try {
      const res = await fetchCreateWizard(data)

      const notificationMessage = !haveSerialNumber
        ? `Virtual ACU ${deviceName} - Created`
        : `ACU ${deviceName || selectedWizard?.data.defaultName} - Added`;
      showNotificationPopUp({
        time: 7000,
        color: "success",
        title: notificationMessage,
      })
    } catch (error) {
      showNotificationPopUp({
        time: 7000,
        color: "danger",
        title: "something went wrong",
      })
    }
    setTimeout(() => {
      handleCancelStep();

    }, 300)
  };

  const handleAddAnyway = async () => {
    const data = {
      name: deviceName,
      description: deviceDescription,
      model: selectedWizard.data.name,
    }
    if (serial_number) {
      data.serial_number = serial_number
    }
    try {
      const res = await fetchCreateWizard(data)
    } catch (error) {

    }
    setShowNotification([
      `ACU ${deviceName} - Added`,
      `ACU ${deviceName} - No network connection`,
    ]);
    handleCancelStep();
  };

  const isDisabledFinishButton = !!serialNumberError;

  const isDisabledNextButton = () => {
    if (serialNumberLoading) return true;
    if (!haveSerialNumber) return false;
    if (step > 1) return !showSearchResult;
    return !!serialNumberError;
  };

  const isDisabledBackButton = () => {
    if (serialNumberError?.status === "42") return false;
    if (serialNumberLoading) return true;
    return step === 3;
  };

  const shouldShowNextButton = () =>
    !haveSerialNumber || (!error && !serialNumberError && step < 3);

  if (!wizardList) return <LoadingSpinner />;

  return (
    <section>
      <MainTabs tabsTexts={TAB_TEXT} activeTabIndex={step} />

      {error ? (
        <DataNotFound />
      ) : (
        <>
          {step === 1 && selectedWizard && (
            <WizardListItems
              itemList={wizardList}
              onClick={(item) => {
                setSelectedWizard({ data: item })
              }}
              id={`${selectedWizard?.data?.id}`}
            />
          )}

          {step > 1 && selectedWizard && (
            <section className="check-serial-number-container">
              {step === 3 ? (
                <WizardFinalStep
                  item={selectedWizard.data}
                  deviceName={deviceName}
                  setDeviceName={setDeviceName}
                  deviceDescription={deviceDescription}
                  setDeviceDescription={setDeviceDescription}
                />
              ) : (
                <WizardSelectType
                  serialNumberLoading={
                    serialNumberError?.status === "45" || serialNumberLoading
                  }
                  selectedWizard={selectedWizard.data}
                  onSearchItem={handleSearchWithSerialNumber}
                  haveSerialNumber={haveSerialNumber}
                  setHaveSerialNumber={setHaveSerialNumber}
                />
              )}

              {(showSearchResult ||
                serialNumberLoading ||
                selectedWizard.data.defaultName) && (
                  <WizardStatus
                    onAddAnyway={handleAddAnyway}
                    status={
                      serialNumberLoading
                        ? "loading"
                        : serialNumberError
                          ? serialNumberError.status === "42"
                            ? "warning"
                            : "error"
                          : "success"
                    }
                    data={
                      serialNumberLoading
                        ? {
                          title: "Please wait.",
                          subTitle: ["The controller is being searched."],
                        }
                        : serialNumberError || selectedWizard.message
                    }
                  />
                )}
            </section>
          )}
        </>
      )}

      <Footer
        item={{
          isCancel: true,
          isNext: step < 3 && shouldShowNextButton(),
          isBack: step > 1,
          isFinish: step === 3 || !shouldShowNextButton(),
        }}
        onBack={() => {
          if (step === 2) {
            setSerialNumberError(null);
            setShowSearchResult(false);
            setDeviceName("");
            setHaveSerialNumber(true);
          }
          handleStepChange(-1);
        }}
        onCancel={() => closeACUModal()}
        onFinish={handleFinishStep}
        onNext={() => {
          if (step === 2 && !haveSerialNumber) {
            handleSearchWithSerialNumber();
          } else {
            handleStepChange(1);
          }
        }}
        itemDisabled={{
          finish: isDisabledFinishButton,
          cancel: serialNumberError?.status !== "42" && step === 3,
          back: isDisabledBackButton(),
          next: isDisabledNextButton(),
        }}
      />

      <Notification messages={showNotification} />
    </section>
  );
};

export default WizardPage;
